import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Toolbar,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  Stack,
  Chip,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import {
  FileDownload as ExportIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Common Report DataGrid Component
 * Features:
 * - Sorting, filtering, pagination
 * - Export to Excel, PDF, CSV
 * - Print functionality
 * - Search
 * - Customizable columns
 * - Responsive design
 */
const ReportDataGrid = ({
  title,
  data = [],
  columns = [],
  loading = false,
  onRefresh,
  exportFileName = 'report',
  pageSize = 25,
  showSearch = true,
  showExport = true,
  showPrint = true,
  customToolbar,
  height = 600,
  density = 'standard', // 'compact', 'standard', 'comfortable'
  checkboxSelection = false,
  onRowClick,
  getRowId,
  sx = {},
}) => {
  const [searchText, setSearchText] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: pageSize,
  });

  // Filter data based on search
  const filteredData = React.useMemo(() => {
    if (!searchText) return data;
    
    return data.filter((row) => {
      return Object.values(row).some((value) => {
        if (value === null || value === undefined) return false;
        return value.toString().toLowerCase().includes(searchText.toLowerCase());
      });
    });
  }, [data, searchText]);

  // Export to Excel
  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    
    // Auto-size columns
    const maxWidth = 50;
    const colWidths = columns.map(col => ({
      wch: Math.min(maxWidth, col.headerName?.length || 10)
    }));
    worksheet['!cols'] = colWidths;
    
    XLSX.writeFile(workbook, `${exportFileName}.xlsx`);
  };

  // Export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    
    // Add title
    doc.setFontSize(16);
    doc.text(title || 'Report', 14, 15);
    
    // Add date
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
    
    // Prepare table data
    const tableColumns = columns.map(col => col.headerName || col.field);
    const tableRows = filteredData.map(row => 
      columns.map(col => {
        const value = row[col.field];
        if (col.valueFormatter) {
          return col.valueFormatter({ value });
        }
        return value !== null && value !== undefined ? value.toString() : '';
      })
    );
    
    // Add table
    doc.autoTable({
      head: [tableColumns],
      body: tableRows,
      startY: 28,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [67, 97, 238], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: 28 },
    });
    
    doc.save(`${exportFileName}.pdf`);
  };

  // Print
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    const tableHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title || 'Report'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #4361ee; margin-bottom: 10px; }
            .meta { color: #666; margin-bottom: 20px; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #4361ee; color: white; font-weight: bold; }
            tr:nth-child(even) { background-color: #f5f5f5; }
            @media print {
              body { padding: 0; }
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${title || 'Report'}</h1>
          <div class="meta">Generated: ${new Date().toLocaleString()}</div>
          <table>
            <thead>
              <tr>
                ${columns.map(col => `<th>${col.headerName || col.field}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(row => `
                <tr>
                  ${columns.map(col => {
                    const value = row[col.field];
                    const displayValue = col.valueFormatter 
                      ? col.valueFormatter({ value })
                      : (value !== null && value !== undefined ? value : '');
                    return `<td>${displayValue}</td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.write(tableHTML);
    printWindow.document.close();
  };

  // Custom Toolbar
  const CustomToolbar = () => {
    return (
      <GridToolbarContainer 
        sx={{ 
          p: 2, 
          borderBottom: '2px solid rgba(67, 97, 238, 0.1)',
          bgcolor: '#fafbfc',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: '100%' }}>
          {/* Left side - Grid tools */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <GridToolbarColumnsButton 
              sx={{ 
                fontSize: '0.875rem',
                color: '#4361ee',
                '&:hover': { bgcolor: 'rgba(67, 97, 238, 0.08)' }
              }} 
            />
            <GridToolbarFilterButton 
              sx={{ 
                fontSize: '0.875rem',
                color: '#4361ee',
                '&:hover': { bgcolor: 'rgba(67, 97, 238, 0.08)' }
              }} 
            />
            <GridToolbarDensitySelector 
              sx={{ 
                fontSize: '0.875rem',
                color: '#4361ee',
                '&:hover': { bgcolor: 'rgba(67, 97, 238, 0.08)' }
              }} 
            />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Right side - Custom actions */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Search */}
            {showSearch && (
              <TextField
                size="small"
                placeholder="Search records..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '1.1rem', color: '#4361ee' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchText && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchText('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 280,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    height: '38px',
                    bgcolor: 'white',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4361ee',
                      }
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4361ee',
                        borderWidth: '2px',
                      }
                    }
                  },
                }}
              />
            )}

            {/* Refresh */}
            {onRefresh && (
              <Tooltip title="Refresh Data" arrow>
                <IconButton 
                  size="small" 
                  onClick={onRefresh} 
                  sx={{ 
                    borderRadius: '6px',
                    bgcolor: 'white',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    '&:hover': { 
                      bgcolor: 'rgba(67, 97, 238, 0.08)',
                      borderColor: '#4361ee',
                    }
                  }}
                >
                  <RefreshIcon sx={{ fontSize: '1.2rem', color: '#4361ee' }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Export buttons */}
            {showExport && (
              <>
                <Tooltip title="Export to Excel" arrow>
                  <IconButton 
                    size="small" 
                    onClick={handleExportExcel} 
                    sx={{ 
                      borderRadius: '6px',
                      bgcolor: 'white',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      '&:hover': { 
                        bgcolor: 'rgba(29, 111, 66, 0.08)',
                        borderColor: '#1D6F42',
                      }
                    }}
                  >
                    <ExcelIcon sx={{ fontSize: '1.2rem', color: '#1D6F42' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export to PDF" arrow>
                  <IconButton 
                    size="small" 
                    onClick={handleExportPDF} 
                    sx={{ 
                      borderRadius: '6px',
                      bgcolor: 'white',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      '&:hover': { 
                        bgcolor: 'rgba(211, 47, 47, 0.08)',
                        borderColor: '#D32F2F',
                      }
                    }}
                  >
                    <PdfIcon sx={{ fontSize: '1.2rem', color: '#D32F2F' }} />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {/* Print */}
            {showPrint && (
              <Tooltip title="Print Report" arrow>
                <IconButton 
                  size="small" 
                  onClick={handlePrint} 
                  sx={{ 
                    borderRadius: '6px',
                    bgcolor: 'white',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    '&:hover': { 
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                      borderColor: 'rgba(0, 0, 0, 0.3)',
                    }
                  }}
                >
                  <PrintIcon sx={{ fontSize: '1.2rem' }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Custom toolbar items */}
            {customToolbar}
          </Box>
        </Box>
      </GridToolbarContainer>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 0,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Header */}
      {title && (
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6" fontWeight="600">
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {filteredData.length} record{filteredData.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* DataGrid */}
      <DataGrid
        rows={filteredData}
        columns={columns}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50, 100]}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick
        onRowClick={onRowClick}
        getRowId={getRowId}
        density={density}
        slots={{
          toolbar: CustomToolbar,
        }}
        sx={{
          border: 'none',
          bgcolor: 'white',
          '& .MuiDataGrid-cell': {
            borderRight: '1px solid rgba(224, 224, 224, 0.8)',
            borderBottom: '1px solid rgba(224, 224, 224, 0.8)',
            fontSize: '0.875rem',
            color: '#2c3e50',
            py: 1.5,
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            },
            '&:last-child': {
              borderRight: 'none',
            },
          },
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: '#f8f9fa',
            borderBottom: '2px solid #4361ee',
            borderRadius: 0,
            minHeight: '48px !important',
            maxHeight: '48px !important',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: '1px solid rgba(224, 224, 224, 1)',
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            },
            '&:last-child': {
              borderRight: 'none',
            },
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            fontSize: '0.875rem',
            color: '#031b4e',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:nth-of-type(even)': {
              bgcolor: '#fafbfc',
            },
            '&:hover': {
              bgcolor: 'rgba(67, 97, 238, 0.06)',
              transform: 'scale(1.001)',
              boxShadow: '0 2px 4px rgba(67, 97, 238, 0.1)',
            },
            '&.Mui-selected': {
              bgcolor: 'rgba(67, 97, 238, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(67, 97, 238, 0.12)',
              },
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '2px solid rgba(0, 0, 0, 0.08)',
            bgcolor: '#f8f9fa',
            minHeight: '52px',
          },
          '& .MuiTablePagination-root': {
            color: '#2c3e50',
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
            fontWeight: 500,
          },
          '& .MuiDataGrid-virtualScroller': {
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                bgcolor: '#a8a8a8',
              },
            },
          },
          '& .MuiDataGrid-overlay': {
            bgcolor: 'rgba(255, 255, 255, 0.9)',
          },
          '& .MuiCircularProgress-root': {
            color: '#4361ee',
          },
          '& .font-weight-bold': {
            fontWeight: 700,
            color: '#031b4e',
          },
          '& .text-error': {
            color: '#d32f2f',
            fontWeight: 600,
          },
          height: height,
        }}
      />
    </Paper>
  );
};

export default ReportDataGrid;
